import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { Search, SortBy } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';
import { Filters, FiltersActive } from 'components/common/filter';
import { breakpoints } from 'styles';

export type AchievementUsersView = 'all-fields' | 'default';

interface Props {
  isSupervisorPage?: boolean;
}

const AchievementUsersSubHeader = ({ isSupervisorPage = false }: Props) => {
  const {
    language,
    messages,
    pageState: { achievement },
    serverConfig: { userUseName }
  } = useReduxState();

  const router = useRouter();

  const { hasExpiration, hasStatus, isGroupAchievement } = useAchievementTypes({
    type: achievement?.type
  });

  const updateView = (newView: AchievementUsersView) => {
    router.replace({ query: { ...router.query, view: newView } });
  };

  const search = (router.query.search as string) || '';
  const view = (router.query.view as AchievementUsersView) || 'default';

  const sortOptions = [
    // The first option is the default selection
    isGroupAchievement && !isSupervisorPage
      ? {
          text: messages.commonMostRecent,
          value: 'recent'
        }
      : userUseName
      ? {
          text: messages.commonName,
          value: 'name'
        }
      : {
          text: messages.commonUsername,
          value: 'username'
        },
    {
      text: messages.commonAddedBy,
      value: 'addedBy'
    },
    {
      text: messages.completionsCompletionDate,
      value: 'completed'
    }
  ];
  if (hasExpiration) {
    sortOptions.push({
      text: messages.commonExpirationDate,
      value: 'expirationDate'
    });
  }
  if (hasStatus) {
    sortOptions.push({
      text: messages.commonStatus,
      value: 'status'
    });
  }

  const defaultSort = sortOptions[0].value;

  const productUserCustomAttributeFilters = achievement.userAttributes?.map(
    (attribute, attributeIndex) => {
      const { max, min, type } = attribute;

      const filterData: React.ComponentProps<
        typeof Filters
      >['customFilters'][0] = {
        allowMultiple: true,
        field: `userAttributes.${attribute.id}`,
        icon: 'tag',
        position: 4 + attributeIndex,
        title: attribute.titleTranslations?.[language] || attribute.title
      };

      if (type === 'Boolean') {
        filterData.items = [
          { label: messages.commonYes, value: 'true' },
          { label: messages.commonNo, value: 'false' },
          { label: messages.commonNotSet, value: 'not set' },
          {
            label: messages.userAttributesNoIncludingNotSet,
            value: 'no including not set'
          }
        ];
      } else if (type === 'Number') {
        filterData.itemsAll = {
          label: messages.groupsUserAttributesAny,
          value: 'anyValueAllowed'
        };
        filterData.items = Array.from(
          Array(Math.max(max - min + 1, 1)),
          (n, i) => ({
            label: `${i + min}`,
            value: `${i + min}`
          })
        );
        filterData.selectAllOnClick = true;
      } else if (type === 'ArrayWithOptions' || type === 'StringWithOptions') {
        filterData.itemsAll = {
          label: messages.groupsUserAttributesAny,
          value: 'anyValueAllowed'
        };
        filterData.items = attribute.options.map((option) => ({
          label: option,
          value: option
        }));
        filterData.selectAllOnClick = true;
      } else {
        // type is String, Array or Text
        filterData.input = true;
      }

      return filterData;
    }
  );

  return (
    <div className="subheader">
      <div className="main">
        <div className="search">
          <Search
            initialValue={search}
            size="header"
            onSearch={({ search }) => {
              router.replace({ query: { ...router.query, search, page: 1 } });
            }}
            placeholder={messages.usersSearchUsers}
          />
        </div>

        <div className="filters">
          <Filters
            allowMultiple={{
              accountAccess: true,
              accountIdentifier: true,
              accountLogin: true,
              accountType: true,
              groups: true,
              userAttributes: true
            }}
            customFilters={[
              {
                allowMultiple: true,
                field: 'completionStatus',
                icon: 'certificate',
                items: [
                  { label: messages.commonCompleted, value: 'yes' },
                  ...(achievement.type === 'external'
                    ? [
                        { label: messages.commonValid, value: 'valid' },
                        { label: messages.commonExpiring, value: 'expiring' },
                        { label: messages.commonExpired, value: 'expired' }
                      ]
                    : []),
                  { label: messages.tasksUsersNotCompleted, value: 'no' },
                  ...(hasStatus && !isGroupAchievement
                    ? [
                        {
                          label: messages.commonNotStarted,
                          value: 'not started'
                        }
                      ]
                    : []),
                  ...(isGroupAchievement
                    ? [
                        {
                          label: messages.commonArchived,
                          value: 'archived'
                        }
                      ]
                    : []),
                  ...(isSupervisorPage
                    ? [{ label: messages.commonNotAdded, value: 'not added' }]
                    : [])
                ],
                position: 3,
                title: messages.commonCompletion
              },
              ...productUserCustomAttributeFilters
            ]}
            filterId="achievement-users"
            filters={{
              accountAccess: true,
              accountIdentifier: true,
              accountLogin: true,
              accountType: true,
              groups: true,
              userAttributes: true
            }}
          />
        </div>

        <div className="view-and-sort">
          {isGroupAchievement && !isSupervisorPage ? null : (
            <DropdownSelectable
              category="lighter"
              defaultText={`${messages.commonSelect}...`}
              defaultValue={view}
              dropdownTextMinWidth="120px"
              onChange={updateView}
              options={[
                { text: messages.commonDefault, value: 'default' },
                { text: messages.commonAllFields, value: 'all-fields' }
              ]}
              selectedValueBefore={`${messages.commonView}:`}
              showSelectedText={true}
              size="header"
              titleMaxWidth={200}
              value={view}
            />
          )}

          <SortBy
            onChange={({ sortBy }: { sortBy: string }) => {
              router.replace({ query: { ...router.query, sortBy, page: 1 } });
            }}
            options={sortOptions}
            value={(router.query.sortBy as string) || defaultSort}
          />
        </div>
      </div>

      <FiltersActive filterId="achievement-users" />

      <style jsx>{`
        .subheader {
          width: 100%;
        }
        .main {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          width: 100%;
        }
        .search {
          width: 100%;
        }
        .filters {
          flex-grow: 1;
        }
        .view-and-sort {
          display: flex;
          gap: 10px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .search {
            width: 240px;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementUsersSubHeader;
